import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent, IconService, SupportedFileTypeService } from '@isophi/core-ui';
import {
  AddressCountryEnum,
  AddressTypeEnum,
  AidPurchaseFormEnum,
  AttendanceEndCodeEnum,
  AttendanceFulfillmentMethodEnum,
  AttendanceStartCodeEnum,
  ChildGroupSuggestion,
  ChildProfileCreate,
  CitiesService,
  CityRead,
  ClassesService,
  DisabilityTypeEnum,
  DisadvantagesEnum,
  DistrictRead,
  DistrictsService,
  EducationCourseChoiceEnum,
  GenderEnum,
  InsuranceCompanyEnum,
  LevelOfTalentEnum,
  MeasureLevelEnum,
  NationalityQualifierEnum,
  SocialCategoryDisadvantageA61Enum,
  SupportingMeasures,
  SupportingMeasuresService,
} from '@isophi/mng-api';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, debounceTime, map, Observable, of, shareReplay, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { BirthNumberService } from '../../../../core/services/birth-number.service';
import { HelperService } from '../../../../core/services/helper.service';
import { SOCIAL_DISADVANTAGE_CODE_PATTERN } from '../social-disadvantage-calculator/social-disadvantage-calculator.util';
import {
  attendanceEndCodeData,
  attendanceFulfillmentMethodData,
  attendanceStartCodeData,
  disadvantagesData,
  educationCourseChoiceData,
  insuranceCompanyData,
  measureLevelData,
  nationalityData,
  nationalityQualifierData,
  selectableSocialCategoryDisadvantageEnum,
  socialCategoryDisadvantageData,
} from './data';

@Component({
  selector: 'app-child-form',
  templateUrl: './child-form.component.html',
  styleUrls: ['./child-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChildFormComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() submitButtonText: string;
  @Input() calculatedIdZnev?: string;
  @Input() childId?: string;

  @Output() formSubmit: EventEmitter<any> = new EventEmitter<any>();

  childForm: FormGroup;

  initialValues;

  profileImageUrl: string;

  groupList$: Observable<ChildGroupSuggestion[]>;

  setGroupList$: Observable<ChildGroupSuggestion[]>;

  genderEnum = GenderEnum;

  nationalities: { [key: string]: string }[] = [];

  nationalitiesQualifier: { [key: string]: string }[] = [];

  insuranceCompany: { [key: string]: string }[] = [];

  attendanceStartCode: { [key: string]: string }[] = [];

  attendanceEndCode: { [key: string]: string }[] = [];

  attendanceFulfillmentMethod: { [key: string]: string }[] = [];

  educationCourseChoice: { [key: string]: string }[] = [];

  socialCategoryDisadvantage: { [key: string]: string }[] = [];

  disadvantages: { [key: string]: string }[] = [];

  measureLevel: { [key: string]: string }[] = [];

  // Education course is locked to "terminated" while an attendance end code is set
  educationCourseLocked = signal(false);

  // Education course value from before the user locked it by picking an end code (undefined = nothing to restore)
  private educationCourseBeforeLock: EducationCourseChoiceEnum | null | undefined;

  showWarning = false;

  addressTypeEnum = AddressTypeEnum;

  showTemporaryAddress = true;

  showContactAddress = true;

  disabilityType: DisabilityTypeEnum[] = [];

  aidPurchaseForm: AidPurchaseFormEnum[] = [];

  levelOfTalent: LevelOfTalentEnum[] = [];

  warningFields: string[] = [];

  addressWarningFields: string[] = [];

  supportingMeasuresList$: Observable<SupportingMeasures[]>;

  nationalityEnum = AddressCountryEnum;

  districts$: Observable<DistrictRead[]>;

  private districts: DistrictRead[] = [];

  cities: CityRead[];

  citiesFiltered: CityRead[];

  cityInput$ = new Subject<string>();

  protected readonly nationalityQualifierEnum = NationalityQualifierEnum;

  protected readonly socialCategoryDisadvantageEnum = SocialCategoryDisadvantageA61Enum;

  private isLoadingFromBackend = false;

  get minDate(): NgbDateStruct {
    const currentDate = new Date();
    return {
      year: currentDate.getFullYear() - 30,
      month: currentDate.getMonth() + 1,
      day: currentDate.getDate(),
    };
  }

  get maxDate(): NgbDateStruct {
    const currentDate = new Date();
    return {
      year: currentDate.getFullYear(),
      month: currentDate.getMonth() + 1,
      day: currentDate.getDate(),
    };
  }

  private _subs: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private formBuilder: FormBuilder,
    public links: RouterLinkFactory,
    private cd: ChangeDetectorRef,
    private spinner: NgxSpinnerService,
    public icons: IconService,
    private classesService: ClassesService,
    private activatedRoute: ActivatedRoute,
    private birthNumberService: BirthNumberService,
    public supportedFileTypeService: SupportedFileTypeService,
    private supportingMeasuresService: SupportingMeasuresService,
    private districtsService: DistrictsService,
    private citiesService: CitiesService
  ) {
    super();
    this.groupList$ = this.classesService.classesSuggestionsList().pipe(shareReplay(1));
    this.setGroupList$ = this.groupList$.pipe(map((data) => JSON.parse(JSON.stringify(data))));
    this.supportingMeasuresList$ = this.supportingMeasuresService.supportingMeasuresList(1000).pipe(map((val) => val.results));
    this.nationalities = this.helperService.prepareData(nationalityData, AddressCountryEnum);
    this.nationalitiesQualifier = this.helperService.prepareData(nationalityQualifierData, NationalityQualifierEnum);
    this.insuranceCompany = this.helperService.prepareData(insuranceCompanyData, InsuranceCompanyEnum);
    this.attendanceStartCode = this.helperService.prepareData(attendanceStartCodeData, AttendanceStartCodeEnum);
    this.attendanceEndCode = this.helperService.prepareData(attendanceEndCodeData, AttendanceEndCodeEnum);
    this.attendanceFulfillmentMethod = this.helperService.prepareData(attendanceFulfillmentMethodData, AttendanceFulfillmentMethodEnum);
    this.educationCourseChoice = this.helperService.prepareData(educationCourseChoiceData, EducationCourseChoiceEnum);
    this.socialCategoryDisadvantage = this.helperService.prepareData(
      socialCategoryDisadvantageData,
      selectableSocialCategoryDisadvantageEnum
    );
    this.disadvantages = this.helperService.prepareData(disadvantagesData, DisadvantagesEnum);
    this.measureLevel = this.helperService.prepareData(measureLevelData, MeasureLevelEnum);
    this.disabilityType = Object.values(DisabilityTypeEnum);
    this.aidPurchaseForm = Object.values(AidPurchaseFormEnum);
    this.levelOfTalent = Object.values(LevelOfTalentEnum);
    this.districts$ = this.districtsService.districtsList().pipe(
      shareReplay(1),
      tap((districts) => {
        this.districts = districts;
      })
    );
    this.cities = [];
    this.citiesFiltered = [];

    this.childForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      nickname: [''],
      profileImage: [''],
      idCode: [''],
      primaryClass: [null],
      setGroups: [null],
      nationality: [this.nationalities[40].value],
      birthNumber: [null, this.birthNumberValidator],
      birthPlace: [''],
      birthDate: [null, Validators.required],
      nationalityQualifier: [this.nationalitiesQualifier[1].value],
      gender: ['', Validators.required],
      insuranceCompany: [null],
      gdprConsent: [false],
      importantNote: [''],
      attendanceStart: [null],
      attendanceEnd: [null],
      attendanceStartCode: [null],
      attendanceEndCode: [null],
      attendanceFulfillmentMethod: [null],
      educationCourseChoice: [null],
      socialCategoryDisadvantage: [this.socialCategoryDisadvantage[0].value],
      socialDisadvantageCode: [''],
      visaLabel: [''],
      isHealthDisadvantaged: [false],
      disadvantages: [[this.disadvantages[0].value], this.validateDisadvantages],
      disadvantageIdentifier: [null],
      measureLevel: [null],
      ignoranceOfLanguage: [false],
      levelOfTalent: [this.levelOfTalent[0]],
      disabilityType: [null],
      hasHeavyDisability: [false],
      izoSpz: [''],
      spzRecommendationIssued: [null],
      spzRecommendationDue: [null],
      supportingMeasures: [null],
      aidPurchaseForm: [null],
      isFundingRequired: [false],
      supportingMeasuresStart: [null],
      supportingMeasuresEnd: [null],
      addresses: this.formBuilder.array([
        this.formBuilder.group({
          addressType: [AddressTypeEnum.Permanent],
          catchment: [false],
          streetAndHouseNumber: [null],
          city: [null],
          country: [this.nationalities[40].value],
          district: [null],
          postalCode: [null],
        }),
      ]),
    });

    // For resetting form;
    this.initialValues = { ...this.childForm.value };

    this.warningFields = [
      'firstName',
      'lastName',
      'birthNumber',
      'birthDate',
      'gender',
      'nationalityQualifier',
      'attendanceStart',
      'socialCategoryDisadvantage',
      'disadvantages',
      'levelOfTalent',
    ];

    this.addressWarningFields = ['city', 'district', 'country'];

    this._subs.push(
      this.childForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
        this.showWarning = this.checkFieldsEmpty(this.warningFields, this.addresses, this.addressWarningFields);
      }),
      this.activatedRoute.queryParams.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((val) => {
        if (val.groupUuid) {
          this.childForm.patchValue({ primaryClass: val.groupUuid });
        }
      }),
      this.childForm.controls.socialCategoryDisadvantage.valueChanges
        .pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((category) => this.updateSocialDisadvantageCodeValidators(category))
    );
    this.updateSocialDisadvantageCodeValidators(this.childForm.get('socialCategoryDisadvantage').value);

    this.setupCityTypeahead();
  }

  get addresses(): FormArray {
    return this.childForm.get('addresses') as FormArray;
  }

  ngOnInit(): void {
    this._subs.push(
      this.childForm.controls.birthNumber.valueChanges
        .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
        .subscribe((value: string) => {
          const birthNumberControl = this.childForm.get('birthNumber');

          if (birthNumberControl.valid && !this.childForm.get('birthDate').value) {
            const newBirthDate = this.birthNumberService.convertBirthNumber(value);
            if (this.childForm.controls.birthDate.value !== newBirthDate) {
              this.childForm.controls.birthDate.setValue(newBirthDate, { emitEvent: false });
              this.cd.markForCheck();
            }
          }
        })
    );

    this._subs.push(
      this.addresses
        .at(0)
        .get('country')
        .valueChanges.pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe(() => {
          if (!this.isLoadingFromBackend) {
            this.addresses.at(0).patchValue({
              district: null,
              city: null,
            });
          }
          this.cd.markForCheck();
        })
    );

    this._subs.push(
      this.addresses
        .at(0)
        .get('district')
        .valueChanges.pipe(takeUntil(this.ngUnsubscribe$))
        .subscribe((district: string) => {
          const selectedCityCode: string = this.addresses.at(0).get('city').value;
          const selectedCityDistrictCode = this.citiesFiltered.find((cityItem) => cityItem.code === selectedCityCode)?.districtCode;
          const selectedDistrictCode = this.districts.find((districtItem) => districtItem.shortName === district)?.code;

          if (selectedCityDistrictCode !== selectedDistrictCode) {
            // Only reset the city if it doesn't belong to the selected district
            this.addresses.at(0).patchValue({
              city: null,
            });
            this.cityInput$.next(null);
            this.citiesFiltered = [];
          }

          this.cd.markForCheck();
        })
    );
  }

  onCityChange(city: CityRead): void {
    this.addresses.at(0).patchValue(
      {
        district: city.districtShortName,
      },
      { emitEvent: false }
    );
    this.cd.markForCheck();
  }

  addAddress(addressType: AddressTypeEnum) {
    const addressFormGroup = this.formBuilder.group({
      addressType: [addressType],
      catchment: [false],
      streetAndHouseNumber: [''],
      city: [''],
      country: [null],
      district: [''],
      postalCode: [''],
    });
    this.addresses.push(addressFormGroup);
  }

  deleteAddress(addressType: AddressTypeEnum) {
    for (let i = 0; i < this.addresses.length; i++) {
      const addressFormGroup = this.addresses.at(i) as FormGroup;
      const addressTypeControl = addressFormGroup.get('addressType');

      if (addressTypeControl.value === addressType) {
        this.addresses.removeAt(i);
        break;
      }
    }
  }

  handleAddresses(addressType: AddressTypeEnum, show: boolean) {
    if (show) {
      this.addAddress(addressType);
    } else {
      this.deleteAddress(addressType);
    }

    if (addressType === this.addressTypeEnum.Contact) {
      this.showContactAddress = !this.showContactAddress;
    }
    if (addressType === this.addressTypeEnum.Temporary) {
      this.showTemporaryAddress = !this.showTemporaryAddress;
    }
  }

  private birthNumberValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (value) {
      const checkValue = /^\d{6}\/\d{4}$/.test(value);

      if (!checkValue) {
        return { invalidBirthNumber: true };
      }
    }
    return null;
  }

  private validateDisadvantages(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (value.length > 1 && value.includes(DisadvantagesEnum.WithoutDisadvantage)) {
      return { invalidDisadvantages: true };
    }
    return null;
  }

  private socialDisadvantageCodeValidator(control: AbstractControl): { [key: string]: any } | null {
    const value = control.value;
    if (value && !SOCIAL_DISADVANTAGE_CODE_PATTERN.test(value)) {
      return { invalidSocialDisadvantageCode: true };
    }
    return null;
  }

  private updateSocialDisadvantageCodeValidators(category: SocialCategoryDisadvantageA61Enum): void {
    const control = this.childForm.get('socialDisadvantageCode');
    if (category === SocialCategoryDisadvantageA61Enum.EvaluationOfSevenKeyAreas) {
      control.setValidators([Validators.required, this.socialDisadvantageCodeValidator]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  onFileChange(event) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.profileImageUrl = reader.result as string;
        this.cd.markForCheck();
      };

      reader.onloadstart = () => {
        this.spinner.show('preview');
      };

      reader.onloadend = () => {
        this.spinner.hide('preview');
      };

      reader.readAsDataURL(file);
      this.childForm.patchValue({ profileImage: file });
    }
  }

  onBirthNumberChange(event) {
    if (this.childForm.get('birthNumber').valid && !this.childForm.get('gender').value) {
      const months = +event.target.value.substring(2, 4);

      if (months > 50) {
        this.childForm.patchValue({ gender: this.genderEnum.Female });
      } else {
        this.childForm.patchValue({ gender: this.genderEnum.Male });
      }
    }
  }

  onPrimaryClassChange(e) {
    if (e) {
      this.setGroupList$ = this.setGroupList$.pipe(map((groups) => groups.filter((group) => group.uuid !== e.uuid)));
      if (this.childForm.value.setGroups && this.childForm.value.setGroups.includes(e.uuid)) {
        this.childForm.patchValue({
          setGroups: [...this.childForm.value.setGroups.filter((groupUuid) => groupUuid !== e.uuid)],
        });
      }
    } else {
      this.setGroupList$ = this.groupList$.pipe(map((data) => JSON.parse(JSON.stringify(data))));
    }
  }

  private checkFieldsEmpty(fieldNames: string[], addresses: FormArray, addressFieldNames: string[]): boolean {
    for (const fieldName of fieldNames) {
      const control = this.childForm.get(fieldName);

      if ((control && !control.value) || control.value?.length === 0) {
        return true;
      }
    }

    for (const address of addresses.controls) {
      const addressFormGroup = address as FormGroup;

      if (addressFormGroup.get('addressType').value === this.addressTypeEnum.Permanent) {
        for (const fieldName of addressFieldNames) {
          const control = addressFormGroup.get(fieldName);

          if (control && !control.value && control) {
            return true;
          }
        }
      }
    }

    return false;
  }

  onSubmit(): void {
    if (this.childForm.valid) {
      const formValue = this.childForm.value;
      const childData = this.formatFormSubmitData(formValue);
      const profileImage = formValue.profileImage;

      if (!childData.setGroups?.length) {
        childData.setGroups = [];
      }

      if (childData.primaryClass) {
        childData.setGroups?.unshift(childData.primaryClass);
      }

      this.formSubmit.emit({ data: childData, file: profileImage });
    }
  }

  private formatFormSubmitData(formValue): ChildProfileCreate {
    const body = {
      firstName: formValue?.firstName,
      lastName: formValue.lastName,
      nickname: formValue.nickname,
      birthDate: this.helperService.formateDate(formValue.birthDate),
      gender: formValue.gender,
      birthNumber: formValue.birthNumber,
      birthPlace: formValue.birthPlace,
      idCode: formValue.idCode,
      nationality: formValue.nationality,
      nationalityQualifier: formValue.nationalityQualifier,
      gdprConsent: formValue.gdprConsent,
      primaryClass: formValue.primaryClass,
      setGroups: formValue.setGroups,
      attendanceStart: this.helperService.formateDate(formValue.attendanceStart),
      attendanceEnd: this.helperService.formateDate(formValue.attendanceEnd),
      attendanceStartCode: formValue.attendanceStartCode,
      attendanceEndCode: formValue.attendanceEndCode,
      attendanceFulfillmentMethod: formValue.attendanceFulfillmentMethod,
      disadvantages: formValue.disadvantages,
      educationCourseChoice: formValue.educationCourseChoice,
      ignoranceOfLanguage: formValue.ignoranceOfLanguage,
      importantNote: formValue.importantNote,
      insuranceCompany: formValue.insuranceCompany,
      isHealthDisadvantaged: formValue.isHealthDisadvantaged,
      measureLevel: formValue.measureLevel,
      socialCategoryDisadvantage: formValue.socialCategoryDisadvantage,
      levelOfTalent: formValue.levelOfTalent,
      disabilityType: formValue.disabilityType,
      hasHeavyDisability: formValue.hasHeavyDisability,
      izoSpz: formValue.izoSpz,
      spzRecommendationIssued: this.helperService.formateDate(formValue.spzRecommendationIssued),
      spzRecommendationDue: this.helperService.formateDate(formValue.spzRecommendationDue),
      supportingMeasures: +formValue.supportingMeasures || null,
      aidPurchaseForm: formValue.aidPurchaseForm,
      isFundingRequired: formValue.isFundingRequired,
      supportingMeasuresStart: this.helperService.formateDate(formValue.supportingMeasuresStart),
      supportingMeasuresEnd: this.helperService.formateDate(formValue.supportingMeasuresEnd),
      addresses: formValue.addresses,
      socialDisadvantageCode: formValue.socialDisadvantageCode,
      visaLabel: formValue.visaLabel,
      idZnev: formValue.disadvantageIdentifier,
    };

    const cityCode = body.addresses.find((address) => address.addressType === AddressTypeEnum.Permanent).city;
    const cityShortName = this.citiesFiltered.find((city) => city.code === cityCode)?.shortName;
    if (cityShortName) {
      body.addresses.find((address) => address.addressType === AddressTypeEnum.Permanent).city = cityShortName;
    }

    return body;
  }

  // For edit child
  setFormData(data): void {
    const { addresses, ...dataToEdit } = data;

    if (dataToEdit.nationality !== AddressCountryEnum.CZ) {
      this.warningFields.splice(this.warningFields.indexOf('birthNumber'), 1);
      this.showWarning = this.checkFieldsEmpty(this.warningFields, this.addresses, this.addressWarningFields);
    }

    this.profileImageUrl = dataToEdit.profileImage;
    dataToEdit.birthDate = this.helperService.parseDate(dataToEdit.birthDate);
    dataToEdit.attendanceStart = this.helperService.parseDate(dataToEdit.attendanceStart);
    dataToEdit.attendanceEnd = this.helperService.parseDate(dataToEdit.attendanceEnd);
    dataToEdit.spzRecommendationIssued = this.helperService.parseDate(dataToEdit.spzRecommendationIssued);
    dataToEdit.spzRecommendationDue = this.helperService.parseDate(dataToEdit.spzRecommendationDue);
    dataToEdit.supportingMeasuresStart = this.helperService.parseDate(dataToEdit.supportingMeasuresStart);
    dataToEdit.supportingMeasuresEnd = this.helperService.parseDate(dataToEdit.supportingMeasuresEnd);
    dataToEdit.setGroups = dataToEdit.childGroups.map((group) => group.uuid).filter((groupUuid) => groupUuid !== dataToEdit.primaryClass);

    if (dataToEdit.primaryClass) {
      this.setGroupList$ = this.setGroupList$.pipe(map((groups) => groups.filter((group) => group.uuid !== dataToEdit.primaryClass)));
    }

    const permanentAddress = addresses.find((address) => address.addressType === AddressTypeEnum.Permanent);
    const otherAddress = addresses.filter((address) => address.addressType !== AddressTypeEnum.Permanent);

    otherAddress.forEach((addressData) => {
      this.handleAddresses(addressData.addressType, true);
    });

    this.isLoadingFromBackend = true;
    this.addresses.patchValue([permanentAddress, ...otherAddress]);
    this.isLoadingFromBackend = false;

    // For Czech addresses, use typeahead to find and set the city code
    if (permanentAddress?.country === this.nationalityEnum.CZ) {
      this.isLoadingFromBackend = true;
      this.cityInput$.next(permanentAddress?.city);
    }

    dataToEdit.disadvantageIdentifier = dataToEdit.idZnev;

    this.childForm.patchValue(dataToEdit);

    // Only lock what is already consistent - a different stored value stays editable until the user changes the end code
    this.educationCourseLocked.set(
      !!dataToEdit.attendanceEndCode && dataToEdit.educationCourseChoice === EducationCourseChoiceEnum.EducationInKindergartenPsPtTerminated
    );
    this.educationCourseBeforeLock = undefined;
  }

  // Called only on user selection/clear (ng-select change event), never when the form is patched with loaded data
  onAttendanceEndCodeChange(endCode: AttendanceEndCodeEnum | null): void {
    const educationCourseChoice = this.childForm.get('educationCourseChoice');

    if (endCode) {
      if (!this.educationCourseLocked()) {
        this.educationCourseBeforeLock = educationCourseChoice.value;
        educationCourseChoice.setValue(EducationCourseChoiceEnum.EducationInKindergartenPsPtTerminated);
        this.educationCourseLocked.set(true);
      }
    } else if (this.educationCourseLocked()) {
      // Restore the value the user had before picking the end code; if the lock came from loaded data, there is nothing to restore
      educationCourseChoice.setValue(this.educationCourseBeforeLock ?? null);
      this.educationCourseBeforeLock = undefined;
      this.educationCourseLocked.set(false);
    }
  }

  resetChildForm(): void {
    this.educationCourseLocked.set(false);
    this.educationCourseBeforeLock = undefined;
    this.childForm.reset(this.initialValues);
    this.profileImageUrl = '';
  }

  ngOnDestroy(): void {
    this._subs.forEach((_sub) => _sub.unsubscribe());
  }

  setupCityTypeahead(): void {
    this._subs.push(
      this.cityInput$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          takeUntil(this.ngUnsubscribe$),
          switchMap((term) => {
            if (term === null || term?.length < 3) {
              return of([]);
            }

            this.citiesFiltered = [];

            return this.citiesService.citiesList(null, term).pipe(
              map((cities) => {
                return cities.map((city) => {
                  return {
                    ...city,
                    shortNameWithDistrict: `${city.shortName} (${city.districtShortName})`,
                  };
                });
              }),
              tap((cities) => {
                this.citiesFiltered = cities;
                if (this.isLoadingFromBackend && term) {
                  const permanentAddress = this.addresses.controls.find(
                    (address) => address.get('addressType').value === AddressTypeEnum.Permanent
                  );
                  const districtShortName = this.addresses.at(0).get('district').value;

                  if (districtShortName) {
                    const cityToPatch = this.citiesFiltered.find(
                      (city) => city.shortName === term && city.districtShortName === districtShortName
                    );

                    permanentAddress.patchValue({
                      city: cityToPatch?.code,
                    });
                  } else {
                    if (permanentAddress.get('country').value === this.nationalityEnum.CZ) {
                      permanentAddress.patchValue({
                        city: cities[0]?.code,
                      });
                    } else {
                      permanentAddress.patchValue({
                        city: cities[0]?.shortName,
                      });
                    }
                  }
                  this.isLoadingFromBackend = false;
                }
              }),
              catchError(() => {
                return of([]);
              })
            );
          })
        )
        .subscribe(() => {
          this.cd.markForCheck();
        })
    );
  }
}
